import { useParams } from 'react-router-dom';
import { publicApi } from '../../lib/api';
import { useAsync } from '../../lib/useAsync';
import DocBrandBand from '../../components/DocBrandBand';
import { Card, Spinner, ErrorNote } from '../../components/ui';
import { money, composeAddress } from '../../lib/format';

export default function PublicInvoice() {
  const { token } = useParams();
  const { data, error, loading, reload } = useAsync(() => publicApi.get('/inv/' + token), [token]);

  if (loading) return <Spinner label="Loading your document" />;
  if (error) {
    return (
      <div className="view">
        <ErrorNote onRetry={reload}>
          We couldn’t open this document. The link may have expired — please contact us for a fresh one.
        </ErrorNote>
      </div>
    );
  }

  const { invoice: i, company: c, customer: cu } = data;
  const settled = i.balance === 0;
  const isReceipt = i.kind === 'receipt';
  const noun = isReceipt ? 'Receipt' : 'Invoice';

  return (
    <article className="doc">
      <DocBrandBand label={noun} company={c} meta={[
        { label: `${noun} No.`, value: i.number },
        { label: 'Date', value: i.date },
      ]} />

      <div className="view">
        <Card tight>
          <div className="sb">{isReceipt ? 'Received from' : 'Invoice to'}</div>
          <div className="nm">{cu?.name}</div>
          <div className="sb">{composeAddress(cu)}</div>
        </Card>

        <Card tight>
          <p className="nm">{isReceipt ? `Payment received by ${c.companyName} for:` : `${c.companyName} provided the following services:`}</p>
          <div style={{ marginTop: 'var(--s-3)' }}>
            {i.lineItems.map((li, j) => (
              <div className="doc-line" key={j}>
                <span className="d">{li.description}</span>
                <span className="a">{money(li.amount)}</span>
              </div>
            ))}
          </div>
          <div className="doc-total"><span className="nm">{isReceipt ? 'Total paid' : 'Total'}</span><span className="v">{money(i.total)}</span></div>
          {isReceipt ? null : (
            <>
              <div className="doc-line" style={{ borderBottom: 'none' }}>
                <span className="d sb">Paid</span><span className="a sb">{money(i.amountPaid || 0)}</span>
              </div>
              <div className="doc-total">
                <span className="nm">Balance due</span>
                <span className="v" style={{ color: settled ? 'var(--green-700)' : 'var(--red-600)' }}>{money(i.balance)}</span>
              </div>
            </>
          )}
        </Card>

        {isReceipt ? (
          <div className="note ok">Thank you — payment received in full.</div>
        ) : settled ? (
          <div className="note ok">Thank you — this invoice is fully paid.</div>
        ) : (
          <div className="note warn">
            Please remit {money(i.balance)} to {c.email || c.companyName}.<br />
            Pay by cheque, cash or bank transfer. Online payments coming soon.
          </div>
        )}
      </div>

      <div className="actions">
        <a className="btn no-print" href={`/api/inv/${token}/pdf?download=1`}>📄 Download PDF</a>
        <button className="btn no-print" onClick={() => window.print()}>🖨 Print</button>
      </div>
    </article>
  );
}
